<template>
  <div>
    <validation-observer ref="rules" tag="div">
      <b-form
        ref="form"
        :style="{ height: trHeight }"
        class="repeater-form"
        @submit.prevent="repeateAgain"
      >
        <p v-if="blocks.length === 0" class="text-center">
          {{
            $tc('Management.Layers.Configure.None', 1, {
              subject: $tc('Management.Layers.Configure.Block', 1),
            })
          }}
        </p>
        <b-row
          v-for="(block, index) in blocks"
          :id="block.id"
          :key="block.id"
          ref="row"
        >
          <b-col lg="3" md="3" sm="12" class="margin-bottom">
            <b-form-group
              :label="$t('Management.Layers.Name') + '*'"
              :label-for="`name-${index}`"
            >
              <validation-provider
                #default="{ errors }"
                :name="`${$t('Management.Layers.Name')} ${index + 1}`"
                rules="required"
              >
                <b-form-input
                  :id="`name-${index}`"
                  v-model="block.name"
                  :placeholder="
                    $t('Global.Write', {
                      subject: $t('Management.Layers.Name'),
                    })
                  "
                  :state="errors.length > 0 ? false : null"
                  autocomplete="off"
                />
                <small class="text-danger" style="height: 15px; display: block">
                  {{ errors[0] }}
                </small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            lg="8"
            md="8"
            sm="11"
            class="d-flex align-items-center justify-content-between flex-wrap"
          >
            <b-form-checkbox
              v-for="key in [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
              ]"
              :key="key"
              v-model="block.week[key.toLowerCase()]"
              style="margin-bottom: 7px"
              :value="true"
              :disabled="locked"
              @change="clearOthers(key.toLowerCase(), index)"
            >
              {{ $t(`Dates.Week.Long.${key}`) }}
            </b-form-checkbox>
          </b-col>

          <b-col
            v-if="!locked"
            lg="1"
            md="1"
            xl="1"
            class="mb-50 d-flex align-items-center justify-content-center"
          >
            <feather-icon
              size="20"
              icon="Trash2Icon"
              class="mr-25 hover-red"
              style="margin-bottom: 0.5rem"
              @click="removeItem(index)"
            />
          </b-col>
          <b-col cols="12">
            <hr />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
    <b-button
      v-if="!locked"
      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
      style="margin-top: 20px"
      variant="outline-primary"
      class="d-flex align-items-center justify-content-center"
      @click="repeateAgain"
    >
      <feather-icon icon="PlusIcon" class="mr-25" size="18" />
      <span>
        {{ $tc('Global.New') }}
        {{ $tc('Management.Layers.Configure.Block', 1) }}
      </span>
    </b-button>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BFormCheckbox,
  BForm,
} from 'bootstrap-vue'
import { heightTransition } from '@core/mixins/ui/transition'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    BForm,
  },

  mixins: [heightTransition],

  props: {
    blocks: {
      type: Array,
      required: true,
    },
    locked: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    required,
    nextId: 0,
  }),

  mounted() {
    this.initTrHeight()
  },
  created() {
    window.addEventListener('resize', this.initTrHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },

  methods: {
    validateWeeks() {
      const weeks = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      }

      this.blocks.forEach(block => {
        Object.keys(weeks).forEach(key => {
          if (block.week[key]) weeks[key] = true
        })
      })

      if (Object.values(weeks).every(x => x === false)) {
        this.$alert.invalid(
          this.$t('Management.Layers.Configure.WeekDayRequired'),
        )
        return false
      }

      return true
    },

    async validate() {
      if (this.blocks.length === 0) {
        this.$alert.invalid(
          this.$tc('Management.Layers.Configure.MinOne', 1, {
            subject: this.$tc('Management.Layers.Configure.Block', 1),
          }),
        )
        return false
      }

      const nameValid = this.$refs.rules.validate()
      this.initTrHeight()
      const weeksValid = this.validateWeeks()

      return weeksValid && nameValid
    },

    clearOthers(key, index) {
      for (let i = 0; i < this.blocks.length; i += 1) {
        if (i !== index) {
          this.blocks[i].week[key] = false
        }
      }
    },

    repeateAgain() {
      this.blocks.push({
        id: (this.nextId += 1),
        name: null,
        week: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        },
      })
      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight)
      })
    },
    removeItem(index) {
      this.blocks.splice(index, 1)
      this.trTrimHeight(this.$refs.row[0].offsetHeight)
    },
    initTrHeight() {
      this.trSetHeight(null)
      this.$nextTick(() => {
        const { scrollHeight } = this.$refs.form
        if (scrollHeight === 0 && this.blocks.length !== 0) {
          this.trSetHeight(this.blocks.length * 103)
          return
        }
        this.trSetHeight(scrollHeight)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: 0.35s height;
}

.margin-bottom {
  margin-bottom: -7px;
}
</style>
